// CreatorAnalyticsScreen — dashboard for creators to understand which content
// drives views, follows, and commission revenue. Routed at /creator/analytics.
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { B } from '../constants';
import { useAuth } from '../AuthContext';
import { analytics, promos as promosAPI } from '../api';
import BatchProgress from '../components/BatchProgress';
import StatCard, { formatNumber } from '../components/StatCard';
import TimeseriesChart from '../components/TimeseriesChart';
import TopContentTable from '../components/TopContentTable';
import PayoutsList, { formatMoney } from '../components/PayoutsList';
import PlatformStatsCard from '../components/PlatformStatsCard';
import MyReelsPanel from '../components/MyReelsPanel';
import LoadingState from '../components/ui/LoadingState';

const RANGE_OPTIONS = [
  { value: '7d',  label: '7 days' },
  { value: '30d', label: '30 days' },
  { value: '90d', label: '90 days' },
  { value: '365d', label: '12 months' },
];

export default function CreatorAnalyticsScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [range, setRange] = useState('30d');
  const [data, setData] = useState({ overview: null, series: [], promos: [], reels: [], funnel: [], payouts: [], platformStats: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [batchJobs, setBatchJobs] = useState([]);
  const [showBatch, setShowBatch] = useState(null);

  useEffect(() => {
    document.title = 'Creator Analytics | GreatBreak';
    return () => { document.title = 'GreatBreak — AI Travel Planner'; };
  }, []);

  useEffect(() => {
    if (!user) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      analytics.overview(),
      analytics.timeseries(range),
      analytics.topPromos(10),
      analytics.topReels(10),
      analytics.funnel(),
      analytics.payouts(),
      promosAPI.listBatches().catch(() => ({ batches: [] })),
    ])
      .then(async ([overview, ts, promos, reels, funnel, payouts, batchData]) => {
        if (cancelled) return;
        let platformStats = {};
        const topPromos = promos.promos || [];
        if (topPromos.length > 0) {
          const statsResults = await Promise.allSettled(
            topPromos.slice(0, 3).map(p => promosAPI.getPlatformStats(p.id))
          );
          for (const result of statsResults) {
            if (result.status === 'fulfilled' && result.value.platformStats) {
              platformStats = result.value.platformStats;
            }
          }
        }
        if (cancelled) return;
        setData({
          overview: overview.overview,
          series: ts.series || [],
          promos: topPromos,
          reels: reels.reels || [],
          funnel: funnel.funnel || [],
          payouts: payouts.payouts || [],
          platformStats,
        });
        setBatchJobs(batchData?.batches || []);
      })
      .catch((err) => { if (!cancelled) setError(err.message || 'Failed to load analytics'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user, range]);

  if (!user) {
    return (
      <div style={{ padding: 40, textAlign: 'center', background: B.sandPattern, minHeight: '60vh' }}>
        <p style={{ color: B.muted, fontFamily: "'Montserrat',sans-serif" }}>
          Please sign in to view your creator analytics.
        </p>
      </div>
    );
  }
  const isCreator = Boolean(user.isCreator || user.isInfluencer);
  if (!isCreator) {
    return (
      <div style={{ padding: 40, textAlign: 'center', background: B.sandPattern, minHeight: '60vh' }}>
        <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 28, color: B.night }}>
          Creator Analytics
        </h1>
        <p style={{ color: B.muted, fontFamily: "'Montserrat',sans-serif", margin: '12px 0' }}>
          Become a creator to track your performance and commission revenue.
        </p>
        <button
          type="button"
          onClick={() => navigate('/?screen=settings')}
          style={{
            background: B.forest, color: '#fff', border: 'none', padding: '10px 20px',
            borderRadius: 24, cursor: 'pointer', fontWeight: 700,
            fontFamily: "'Montserrat',sans-serif",
          }}
        >
          Open settings
        </button>
      </div>
    );
  }

  const o = data.overview || {};

  return (
    <div style={{ background: B.sandPattern, minHeight: '100vh', padding: '40px 24px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <p style={{ fontFamily: "'Montserrat',sans-serif", letterSpacing: 2, fontSize: 12, fontWeight: 700, color: B.forest, marginBottom: 4, textTransform: 'uppercase' }}>
              Creator Dashboard
            </p>
            <h1 style={{ fontFamily: "'Playfair Display',serif", fontSize: 36, fontWeight: 700, color: B.night, margin: 0 }}>
              Your analytics
            </h1>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {RANGE_OPTIONS.map((r) => (
              <button
                key={r.value}
                type="button"
                onClick={() => setRange(r.value)}
                style={{
                  padding: '8px 14px', borderRadius: 20, cursor: 'pointer',
                  border: `1px solid ${range === r.value ? B.forest : '#dcd6cc'}`,
                  background: range === r.value ? B.forest : '#fff',
                  color: range === r.value ? '#fff' : B.night,
                  fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 600,
                }}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <div style={{ background: 'rgba(220,53,69,0.1)', border: '1px solid rgba(220,53,69,0.25)', borderRadius: 12, padding: '12px 16px', marginBottom: 16, color: '#dc3545', fontFamily: "'Montserrat',sans-serif", fontSize: 13 }}>
            ⚠️ {error}
          </div>
        )}

        {loading ? (
          <LoadingState variant="spinner" message="Loading analytics…" />
        ) : (
          <>
            {/* Overview cards */}
            <div style={{
              display: 'grid', gap: 12,
              gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))',
              marginBottom: 24,
            }}>
              <StatCard label="Promo views" value={o.promoViews || 0} accent={B.forest} />
              <StatCard label="Promo likes" value={o.promoLikes || 0} accent={B.amber} />
              <StatCard label="Reel views"  value={o.reelViews || 0}  accent={B.sky} />
              <StatCard label="Followers"   value={o.followers || 0}  accent={B.mint} />
              <StatCard label="Bookings"    value={o.bookings || 0}   accent={B.night} />
              <StatCard
                label="Commission"
                value={o.commissionCents || 0}
                formatter={(v) => formatMoney(v)}
                hint={`${formatMoney(o.pendingCommissionCents || 0)} pending`}
                accent={B.forest}
              />
            </div>

            {/* Timeseries */}
            <section style={{ marginBottom: 32 }}>
              <h2 style={{
                fontFamily: "'Playfair Display',serif", color: B.night, fontSize: 22, marginBottom: 12,
              }}>
                Activity over time
              </h2>
              <TimeseriesChart
                series={data.series}
                metrics={[
                  { key: 'promoViews', label: 'Promo views', color: B.forest },
                  { key: 'reelViews',  label: 'Reel views',  color: B.sky },
                  { key: 'bookings',   label: 'Bookings',    color: B.amber },
                ]}
              />
            </section>

            {/* Funnel */}
            {data.funnel.length > 0 && (
              <section style={{ marginBottom: 32 }}>
                <h2 style={{
                  fontFamily: "'Playfair Display',serif", color: B.night, fontSize: 22, marginBottom: 12,
                }}>
                  Conversion funnel
                </h2>
                <div style={{
                  background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'space-around',
                  fontFamily: "'Montserrat',sans-serif",
                }}>
                  {data.funnel.map((stage) => (
                    <div key={stage.stage} style={{ textAlign: 'center', flex: '1 1 120px' }}>
                      <div style={{ fontSize: 11, color: B.muted, textTransform: 'uppercase', letterSpacing: 1 }}>
                        {stage.stage}
                      </div>
                      <div style={{ fontSize: 24, color: B.night, fontWeight: 700, fontFamily: "'Playfair Display',serif" }}>
                        {formatNumber(stage.value)}
                      </div>
                      <div style={{ fontSize: 12, color: B.muted }}>
                        {stage.conversionPct}% conv.
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Platform performance */}
            {Object.keys(data.platformStats).length > 0 && (
              <section style={{ marginBottom: 32 }}>
                <h2 style={{
                  fontFamily: "'Playfair Display',serif", color: B.night, fontSize: 22, marginBottom: 12,
                }}>
                  📊 Platform Performance
                </h2>
                <div style={{
                  display: 'grid', gap: 12,
                  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
                }}>
                  {Object.entries(data.platformStats).map(([platform, stats]) => {
                    const allStats = Object.values(data.platformStats);
                    const maxShares = Math.max(...allStats.map(s => s.sharesCount), 0);
                    const isBest = stats.sharesCount > 0 && stats.sharesCount >= maxShares;
                    return (
                      <PlatformStatsCard
                        key={platform}
                        platform={platform}
                        stats={stats}
                        isBest={isBest}
                      />
                    );
                  })}
                </div>
              </section>
            )}

            {/* Reel lifecycle */}
            <section style={{ marginBottom: 32 }}>
              <h2 style={{
                fontFamily: "'Playfair Display',serif", color: B.night, fontSize: 22, marginBottom: 12,
              }}>
                Your reels
              </h2>
              <div style={{
                background: '#fff', borderRadius: 12, padding: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              }}>
                <MyReelsPanel />
              </div>
            </section>

            {/* Top promos */}
            <TopContentTable
              title="Top promos"
              rows={data.promos}
              columns={[
                { key: 'views', label: 'Views' },
                { key: 'likes', label: 'Likes' },
                { key: 'attributedBookings', label: 'Bookings' },
              ]}
              emptyMessage="Post your first promo to see analytics here."
            />

            {/* Top reels */}
            <TopContentTable
              title="Top recap reels"
              rows={data.reels}
              columns={[{ key: 'views', label: 'Views' }]}
              emptyMessage="Publish a recap reel to see analytics here."
            />

            {/* Payouts */}
            <section>
              <h2 style={{
                fontFamily: "'Playfair Display',serif", color: B.night, fontSize: 22, marginBottom: 12,
              }}>
                Commission history
              </h2>
              <PayoutsList payouts={data.payouts} />
            </section>

            {/* Batch History */}
            {batchJobs.length > 0 && (
              <section style={{ marginTop: 32 }}>
                <h2 style={{
                  fontFamily: "'Playfair Display',serif", color: B.night, fontSize: 22, marginBottom: 12,
                }}>
                  📦 Batch Upload History
                </h2>
                <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
                  {batchJobs.map(b => (
                    <div key={b.id} onClick={() => setShowBatch(b.id)}
                      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderBottom: '1px solid #f0f0f0', cursor: 'pointer' }}>
                      <span style={{ fontSize: 20 }}>
                        {b.status === 'COMPLETED' ? '✅' : b.status === 'FAILED' ? '❌' : b.status === 'PROCESSING' ? '⏳' : '⏸️'}
                      </span>
                      <div style={{ flex: 1 }}>
                        <p style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 13, fontWeight: 600, color: B.night, margin: 0 }}>
                          Batch #{b.id}
                        </p>
                        <p style={{ fontSize: 11, color: B.muted, margin: '2px 0 0' }}>
                          {b.totalVideos} videos · {b.completedVideos} done · {b.failedVideos} failed
                        </p>
                      </div>
                      <span style={{ fontSize: 11, color: B.muted, fontFamily: "'Montserrat',sans-serif" }}>
                        {new Date(b.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {showBatch && (
              <BatchProgress batchId={showBatch} onClose={() => setShowBatch(null)} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
